import { c as _c } from "react/compiler-runtime";
import { useRef } from "react";
import { ChevronRight, ImageOff } from "lucide-react";
import { useTranslations } from "next-intl";
import { cn } from "@/lib/utils";
import { useEdgeFadeMask } from "@/hooks/useEdgeFadeMask";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useRoomRail, type RoomRailItem } from "./useRoomRail";
export type { RoomRailItem };
export function RoomRail(t0) {
  const $ = _c(33);
  const {
    rooms,
    activeSlug,
    onSelect,
    filterParams,
    className
  } = t0;
  const t = useTranslations("modules.realEstateDetail");
  const ref = useRef(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      axis: "x"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  useEdgeFadeMask(ref, t1);
  let t2;
  if ($[1] !== activeSlug || $[2] !== filterParams || $[3] !== rooms) {
    t2 = {
      rooms,
      activeSlug,
      filterParams
    };
    $[1] = activeSlug;
    $[2] = filterParams;
    $[3] = rooms;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const {
    matching,
    visibleNonMatching,
    hiddenCount,
    setShowOthers,
    reasonText
  } = useRoomRail(t2);
  let t3;
  if ($[5] !== activeSlug || $[6] !== onSelect || $[7] !== reasonText) {
    t3 = (room, t4, t5) => {
      const dimmed = t4 === undefined ? false : t4;
      const index = t5 === undefined ? 0 : t5;
      const active = room.slug === activeSlug;
      const price = room.rentPrice ? `${Number(room.rentPrice).toLocaleString("pt-PT")}€` : null;
      const button = <button key={room.slug} type="button" role="tab" aria-selected={active} onClick={() => onSelect(room.slug)} style={dimmed ? {
        animationDelay: `${index * 45}ms`
      } : undefined} className={cn("relative w-32 shrink-0 cursor-pointer overflow-hidden rounded-xl border bg-card text-left transition-opacity", active ? "border-transparent" : "border-border", dimmed && !active && "opacity-45 hover:opacity-100", dimmed && "duration-300 animate-in fade-in slide-in-from-left-2 fill-mode-both")}>{active && <span aria-hidden={true} className="pointer-events-none absolute inset-0 z-10 rounded-xl ring-2 ring-inset ring-primary" />}<div className="relative h-20 w-full bg-muted">{room.thumbnail ? <img src={room.thumbnail} alt="" className="h-full w-full object-cover" /> : <div className="flex h-full w-full items-center justify-center text-faint-foreground"><ImageOff className="h-5 w-5" /></div>}{price && <span className="absolute bottom-1.5 left-1.5 rounded-md bg-card/95 px-2 py-0.5 text-xs font-extrabold text-foreground-strong shadow">{price}</span>}</div><div className="px-2 py-1.5"><div className="truncate text-xs font-bold text-foreground">{room.title}</div></div></button>;
      if (!dimmed) {
        return button;
      }
      return <Tooltip key={room.slug} data-sentry-element="Tooltip" data-sentry-component="renderRoom" data-sentry-source-file="RoomRail.tsx"><TooltipTrigger asChild={true} data-sentry-element="TooltipTrigger" data-sentry-source-file="RoomRail.tsx">{button}</TooltipTrigger><TooltipContent side="top" className="max-w-52" data-sentry-element="TooltipContent" data-sentry-source-file="RoomRail.tsx">{reasonText(room)}</TooltipContent></Tooltip>;
    };
    $[5] = activeSlug;
    $[6] = onSelect;
    $[7] = reasonText;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const renderRoom = t3;
  let t4;
  if ($[9] !== t) {
    t4 = t("roomsInThisHouse");
    $[9] = t;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== className) {
    t5 = cn("-mx-1.5 flex items-stretch gap-1 overflow-x-auto scrollbar-none px-1.5 py-0.5", className);
    $[11] = className;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== matching || $[14] !== renderRoom) {
    let t7;
    if ($[16] !== renderRoom) {
      t7 = room_0 => renderRoom(room_0);
      $[16] = renderRoom;
      $[17] = t7;
    } else {
      t7 = $[17];
    }
    t6 = matching.map(t7);
    $[13] = matching;
    $[14] = renderRoom;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[18] !== hiddenCount || $[19] !== setShowOthers || $[20] !== t) {
    t7 = hiddenCount > 0 && <button type="button" onClick={() => setShowOthers(true)} className="flex w-32 shrink-0 cursor-pointer flex-col items-center justify-center gap-1 rounded-xl border border-dashed border-border bg-card/50 px-2 text-center text-faint-foreground transition-colors hover:text-foreground"><ChevronRight className="h-4 w-4" /><span className="text-[11px] font-semibold leading-tight">{t("showRoomsOutsideFilters", {
          count: hiddenCount
        })}</span></button>;
    $[18] = hiddenCount;
    $[19] = setShowOthers;
    $[20] = t;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  let t8;
  if ($[22] !== renderRoom || $[23] !== visibleNonMatching) {
    let t9;
    if ($[25] !== renderRoom) {
      t9 = (room_1, i) => renderRoom(room_1, true, i);
      $[25] = renderRoom;
      $[26] = t9;
    } else {
      t9 = $[26];
    }
    t8 = visibleNonMatching.map(t9);
    $[22] = renderRoom;
    $[23] = visibleNonMatching;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  let t9;
  if ($[27] !== t4 || $[28] !== t5 || $[29] !== t6 || $[30] !== t7 || $[31] !== t8) {
    t9 = <TooltipProvider delayDuration={150} data-sentry-element="TooltipProvider" data-sentry-component="RoomRail" data-sentry-source-file="RoomRail.tsx"><div ref={ref} role="tablist" aria-label={t4} className={t5}>{t6}{t7}{t8}</div></TooltipProvider>;
    $[27] = t4;
    $[28] = t5;
    $[29] = t6;
    $[30] = t7;
    $[31] = t8;
    $[32] = t9;
  } else {
    t9 = $[32];
  }
  return t9;
}